import { Fragment, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  TASK_STATUSES, TASK_STATUS_META, TASK_PRIORITY_META,
  patchTask, type TaskDto, type TaskStatus, type TaskPriority,
} from '../../api/tasks';
import {
  createCustomField, deleteCustomField, listCustomFields, patchCustomField, setCustomFieldValue,
  type CustomFieldDto, type CustomFieldOption, type CustomFieldType, type CustomFieldValue,
} from '../../api/task-extras';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { TaskStatusIcon } from '../../components/shared';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import { AddFieldMenu, FieldCell } from './custom-field-ui';
// 화면구성도 V0.1 슬라이드 9 — 담당자·기한은 아이콘 + 팝오버로 그 자리에서 고친다.
import { AssigneeCell, DueCell, type CellMember } from './task-cell-popovers';
import './TaskTableView.css';

interface TaskTableViewProps {
  tasks: TaskDto[];
  loading: boolean;
  projectId: string;
  teamId: string | null;
  /** 행 클릭 → 태스크 상세 레이어팝업 (칸반과 같은 동선). */
  onOpenTask?: (task: TaskDto) => void;
  /** 인라인 편집(시작일·진행률) 저장 결과를 부모 목록에 반영. */
  onTaskUpdated?: (task: TaskDto) => void;
  /**
   * 커스텀 필드 값 재조회 트리거 (2026-08-21).
   * 태스크 상세의 '추가 정보' 에서 값을 고치면 부모가 이 수를 올린다 — 그때 값을 다시 읽는다.
   * (커스텀 필드 값은 TaskDto 에 없어 tasks prop 갱신만으로는 여기까지 전해지지 않는다.)
   */
  customFieldsVersion?: number;
}

/** 상태 그룹당 처음에 보여 줄 행 수 — 나머지는 "+ N건 더 보기". */
const GROUP_PREVIEW = 5;

/**
 * TaskTableView (AW-002-02, 2026-08-18) — 프로젝트 상세 **목록보기**.
 *
 * 칸반과 같은 업무를 상태별로 묶은 표로 본다. 설계서의 두 가지가 핵심이다:
 *   ① 표 맨 오른쪽 끝의 ＋ — 사용자가 원하는 타입의 새 컬럼(커스텀 필드)을 직접 만든다.
 *   ② 상단 "내 팀" 패널 없음 — 헤더의 소형 배지로 칸반과 통일(TaskPage 가 이미 그렇게 그린다).
 *
 * 기존 `TaskListView` 는 건드리지 않고 신규 파일로 둔다(소스 회귀 금지). 그쪽은 상태 셀렉트가
 * 달린 평면 표라 설계가 다르다 — 롤백이 필요하면 툴바에서 이 컴포넌트만 바꿔 끼우면 된다.
 */
export function TaskTableView({
  tasks, loading, projectId, teamId, onOpenTask, onTaskUpdated, customFieldsVersion = 0,
}: TaskTableViewProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [fields, setFields] = useState<CustomFieldDto[]>([]);
  const [values, setValues] = useState<Record<string, CustomFieldValue>>({});
  const [members, setMembers] = useState<TeamMemberDto[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<{ taskId: string; col: 'startDate' | 'progress' } | null>(null);

  // 커스텀 필드 정의 + 값 — 값은 (fieldId, taskId) 를 한 키로 눌러 담아 셀 조회를 O(1) 로 둔다.
  const loadFields = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await listCustomFields(projectId);
      setFields(res.fields);
      const map: Record<string, CustomFieldValue> = {};
      for (const v of res.values) map[`${v.fieldId}:${v.taskId}`] = v.value;
      setValues(map);
    } catch {
      setFields([]);
      setValues({});
    }
  }, [projectId]);

  // customFieldsVersion 이 오르면 다시 읽는다 — 상세('추가 정보')에서 고친 값이 표에 들어온다.
  useEffect(() => { void loadFields(); }, [loadFields, customFieldsVersion]);

  useEffect(() => {
    let alive = true;
    if (!teamId) { setMembers([]); return; }
    void listTeamMembers(teamId)
      .then((list) => { if (alive) setMembers(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [teamId]);

  const memberOptions = useMemo(
    () => members.map((m) => ({ userId: m.userId, name: m.name })),
    [members],
  );
  // 담당자 팝오버가 쓰는 목록 — 이름 + 부서/직책(검색 대상).
  const cellMembers = useMemo<CellMember[]>(
    () => members.map((m) => ({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl, subtitle: m.email })),
    [members],
  );
  const byStatus = useMemo(() => {
    const g: Record<TaskStatus, TaskDto[]> = { TODO: [], IN_PROGRESS: [], REVIEW: [], DONE: [] };
    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      const s = (TASK_STATUSES as string[]).includes(t.status) ? (t.status as TaskStatus) : 'TODO';
      g[s].push(t);
    }
    for (const arr of Object.values(g)) arr.sort((a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : 1));
    return g;
  }, [tasks]);

  async function saveField(taskId: string, field: CustomFieldDto, next: CustomFieldValue) {
    const key = `${field.id}:${taskId}`;
    const prev = values[key] ?? null;
    setValues((v) => ({ ...v, [key]: next })); // optimistic
    try {
      await setCustomFieldValue(taskId, field.id, next);
    } catch {
      setValues((v) => ({ ...v, [key]: prev }));
    }
  }

  async function savePatch(
    task: TaskDto,
    input: { startDate?: string | null; progress?: number; dueDate?: string | null; assigneeUserIds?: string[] },
  ) {
    setEditing(null);
    try {
      const updated = await patchTask(task.id, input);
      onTaskUpdated?.(updated);
    } catch {
      /* 실패 시 값은 그대로 — 다음 조회에서 서버 값으로 되돌아온다. */
    }
  }

  const totalCols = 7 + fields.length + 1;

  return (
    <div className="tt-wrap scrollbar-thin" aria-busy={loading}>
      <table className="tt-table">
        <thead>
          <tr>
            <th className="tt-th-title">{tTask('task.table.col_task')}</th>
            <th>{tTask('task.table.col_assignee')}</th>
            <th>{tTask('task.table.col_priority')}</th>
            <th>{tTask('task.table.col_start')}</th>
            <th>{tTask('task.table.col_progress')}</th>
            <th>{tTask('task.table.col_tags')}</th>
            <th>{tTask('task.table.col_due')}</th>
            {fields.map((f) => <th key={f.id}>{f.name}</th>)}
            <th className="tt-th-add">
              <AddFieldMenu
                fields={fields}
                onCreate={async (input: { name: string; fieldType: CustomFieldType; options?: CustomFieldOption[] }) => {
                  await createCustomField(projectId, input);
                  await loadFields();
                }}
                onDelete={async (fieldId) => {
                  await deleteCustomField(fieldId);
                  await loadFields();
                }}
                onSetUnit={async (fieldId, unit) => {
                  // 단위는 필드 정의의 options[0] 한 칸 — 빈 값이면 단위 없음.
                  await patchCustomField(fieldId, { options: unit ? [{ label: unit }] : [] });
                  await loadFields();
                }}
                onRename={async (fieldId, name) => {
                  // 이름 바꾸기 (2026-09-11) — 표 머리글도 새 이름으로 다시 읽는다.
                  await patchCustomField(fieldId, { name });
                  await loadFields();
                }}
              />
            </th>
          </tr>
        </thead>

        <tbody>
          {TASK_STATUSES.map((status) => {
            const rows = byStatus[status];
            const meta = TASK_STATUS_META[status];
            const isOpen = expanded[status] ?? false;
            const shown = isOpen ? rows : rows.slice(0, GROUP_PREVIEW);
            const rest = rows.length - shown.length;
            return (
              <Fragment key={status}>
                <tr className="tt-group">
                  <td colSpan={totalCols}>
                    <span className="tt-group-dot" style={{ background: meta.color }} aria-hidden />
                    <span className="tt-group-ico" aria-hidden><TaskStatusIcon status={status} size={13} /></span>
                    <span className="tt-group-label">{meta.label}</span>
                    <span className="tt-group-count">{rows.length}</span>
                  </td>
                </tr>

                {rows.length === 0 && (
                  <tr className="tt-empty">
                    <td colSpan={totalCols}>{tTask('task.kanban.empty_column')}</td>
                  </tr>
                )}

                {shown.map((t) => {
                  const pm = TASK_PRIORITY_META[t.priority as TaskPriority] ?? null;
                  const overdue = isOverdue(t.dueDate, t.status);
                  const progress = t.progress ?? 0;
                  return (
                    <tr className="tt-row" key={t.id}>
                      <td className="tt-td-title">
                        <button type="button" className="tt-title-btn" onClick={() => onOpenTask?.(t)}>
                          {t.title}
                        </button>
                      </td>

                      <td>
                        <AssigneeCell
                          assignees={t.assignees ?? []}
                          members={cellMembers}
                          onChange={(ids) => void savePatch(t, { assigneeUserIds: ids })}
                        />
                      </td>

                      <td>
                        {pm && <span className="tt-pri" style={{ color: pm.color }}>{pm.label}</span>}
                      </td>

                      <td>
                        {editing?.taskId === t.id && editing.col === 'startDate' ? (
                          <input
                            type="date"
                            className="tt-cell-input"
                            defaultValue={t.startDate ?? ''}
                            autoFocus
                            onBlur={(e) => void savePatch(t, { startDate: e.target.value || null })}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
                          />
                        ) : (
                          <button
                            type="button"
                            className={`tt-cell-view${t.startDate ? '' : ' tt-cell-view--empty'}`}
                            onClick={() => setEditing({ taskId: t.id, col: 'startDate' })}
                          >
                            {t.startDate ? shortDate(t.startDate) : '-'}
                          </button>
                        )}
                      </td>

                      <td>
                        {editing?.taskId === t.id && editing.col === 'progress' ? (
                          <input
                            type="number"
                            className="tt-cell-input"
                            min={0}
                            max={100}
                            defaultValue={progress}
                            autoFocus
                            onBlur={(e) => void savePatch(t, { progress: clampPercent(e.target.value) })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              if (e.key === 'Escape') setEditing(null);
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="tt-progress"
                            onClick={() => setEditing({ taskId: t.id, col: 'progress' })}
                            aria-label={tTask('task.progress.aria', { percent: progress })}
                          >
                            <span className="tt-progress-bar">
                              <span className="tt-progress-fill" style={{ width: `${progress}%` }} />
                            </span>
                            <small>{progress}%</small>
                          </button>
                        )}
                      </td>

                      <td>
                        {(t.tags ?? []).length === 0
                          ? <span className="tt-dash">-</span>
                          : <span className="tt-chips">{t.tags.map((tag) => <span key={tag} className="tt-chip">{tag}</span>)}</span>}
                      </td>

                      <td>
                        <DueCell
                          dueDate={t.dueDate}
                          startDate={t.startDate ?? null}
                          overdue={overdue}
                          label={t.dueDate ? dueLabel(t.dueDate, overdue) : null}
                          onChange={(next) => void savePatch(t, next)}
                        />
                      </td>

                      {fields.map((f) => (
                        <td key={f.id}>
                          <FieldCell
                            field={f}
                            value={values[`${f.id}:${t.id}`] ?? null}
                            members={memberOptions}
                            onChange={(next) => void saveField(t.id, f, next)}
                          />
                        </td>
                      ))}
                      <td />
                    </tr>
                  );
                })}

                {rest > 0 && (
                  <tr className="tt-more">
                    <td colSpan={totalCols}>
                      <button type="button" onClick={() => setExpanded((e) => ({ ...e, [status]: true }))}>
                        {tTask('task.table.more', { n: String(rest) })}
                      </button>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function clampPercent(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** 'YYYY-MM-DD' → 'M/D' (설계서 표기). */
function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function isOverdue(dueDate: string | null | undefined, status: string): boolean {
  if (!dueDate || status === 'DONE') return false;
  return dueDate < new Date().toISOString().slice(0, 10);
}

/** 지연이면 'N일 지연', 아니면 날짜. 설계서의 기한 표기 규칙(상태 문구 우선)과 같은 결. */
export function dueLabel(dueDate: string, overdue: boolean): string {
  if (!overdue) return shortDate(dueDate);
  const due = new Date(dueDate);
  const today = new Date();
  const a = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.max(1, Math.round((b - a) / 86_400_000));
  return tTask('task.table.overdue', { n: String(days) });
}
